import { fetchApi } from '../api/fetchApi';
import { trackError } from '../tracking/errorTracker';
import { getRuntimeMode, NATIVE_RUNTIME_MODE } from './runtimeMode';
const IMPORT_MAP_URL = '/import-map.json';
const getImportShim = () => window.importShim;
const getImportMapOverrides = () => window.importMapOverrides;
/**
 * Native mode is deliberately fail-fast: a missing loader means its runtime
 * script never loaded, and falling back would leave the page mixing loaders.
 */
const missingCapability = (capability, specifier) => {
    const runtimeMode = getRuntimeMode();
    const error = new Error(`Runtime loader: "${capability}" is not available in "${runtimeMode}" runtime mode`);
    trackError(error, { capability, specifier, runtimeMode });
    return error;
};
/**
 * Native mode uses `importShim`, never a bare `import()`. `es-module-shims`
 * only engages its polyfill on a static link-time failure, so a runtime-only
 * dynamic import can fail on browsers in our range that lack import maps.
 */
export const loadSpaModule = (specifier) => {
    if (getRuntimeMode() === NATIVE_RUNTIME_MODE) {
        const importShim = getImportShim();
        if (typeof importShim !== 'function') {
            return Promise.reject(missingCapability('window.importShim', specifier));
        }
        return importShim(specifier);
    }
    if (typeof System === 'undefined' || typeof System.import !== 'function') {
        return Promise.reject(missingCapability('System.import', specifier));
    }
    return System.import(specifier);
};
/**
 * Throws when the specifier cannot be resolved, matching `System.resolve`.
 * Callers depend on this: `<specifier>-css` is absent for modules that ship no
 * stylesheet, and they catch the throw and carry on.
 */
export const resolveSpaSpecifier = (specifier) => {
    if (getRuntimeMode() === NATIVE_RUNTIME_MODE) {
        const importShim = getImportShim();
        if (!importShim || typeof importShim.resolve !== 'function') {
            throw missingCapability('window.importShim.resolve', specifier);
        }
        return importShim.resolve(specifier);
    }
    if (typeof System === 'undefined' || typeof System.resolve !== 'function') {
        throw missingCapability('System.resolve', specifier);
    }
    return System.resolve(specifier);
};
let importMapFetch = null;
// Self-contained rather than reusing `getImports` from `spaUtilities`, so the
// unused import map group helpers can be deleted without touching the loader.
const fetchImportMapImports = () => {
    if (!importMapFetch) {
        importMapFetch = fetchApi(IMPORT_MAP_URL)
            .then(response => {
            if (!response.ok) {
                throw new Error('Failed to fetch import map');
            }
            return response.json();
        })
            .then(importMap => (importMap === null || importMap === void 0 ? void 0 : importMap.imports) || {})
            .catch(e => {
            trackError(e);
            return {};
        });
    }
    return importMapFetch;
};
/**
 * Reads the post-override map from `import-map-overrides` rather than
 * re-fetching `/import-map.json`: Frontage inlines the map in native mode, so
 * the endpoint can drift from the page during frequent deployments. The fetch
 * remains only as a fallback for pages without the override runtime.
 */
export const getEffectiveImportMap = async () => {
    const importMapOverrides = getImportMapOverrides();
    if (typeof (importMapOverrides === null || importMapOverrides === void 0 ? void 0 : importMapOverrides.getCurrentPageMap) === 'function') {
        try {
            const currentPageMap = await importMapOverrides.getCurrentPageMap();
            return { imports: (currentPageMap === null || currentPageMap === void 0 ? void 0 : currentPageMap.imports) || {} };
        }
        catch (e) {
            trackError(e, {
                runtimeMode: getRuntimeMode(),
                source: 'importMapOverrides.getCurrentPageMap',
            });
        }
    }
    return { imports: await fetchImportMapImports() };
};
